import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './RegistoHoras.css';

/* ============================================================
   REGISTO DE HORAS — acesso mobile
   O trabalhador regista as horas do dia (normais + extra, obra, notas).
   Um registo por dia: voltar a guardar o mesmo dia substitui.
   Reflete-se em Recursos Humanos > Registo de Horas.
   ============================================================ */

const hojeISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const fmtDia = (iso) => {
    const [a, m, d] = String(iso).substring(0, 10).split('-');
    const dt = new Date(+a, +m - 1, +d);
    return { dia: `${d}/${m}`, semana: DIAS[dt.getDay()] };
};
const num = (v) => Number(v || 0);
const obraLabel = (o) => [o.posicao, o.nome || o.descricao || o.cliente].filter(Boolean).join(' - ');

const FORM_VAZIO = { data: hojeISO(), horas: '8', horas_extra: '', obra_id: '', observacoes: '' };

const RegistoHoras = () => {
    const navigate = useNavigate();
    const [trab, setTrab] = useState(undefined);   // undefined = a carregar; null = conta sem trabalhador ligado
    const [registos, setRegistos] = useState([]);
    const [obras, setObras] = useState([]);
    const [form, setForm] = useState(FORM_VAZIO);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);           // { tipo: 'ok' | 'erro', texto }

    const carregar = useCallback(async () => {
        try {
            const [h, o] = await Promise.all([
                api.get('/horas/me'),
                api.get('/obras').catch(() => ({ data: [] })),
            ]);
            setTrab(h.data.trabalhador);
            setRegistos(h.data.registos || []);
            const lista = o.data?.obras || o.data || [];
            const ativas = lista.filter(x => x.estado !== 'Concluida' && x.estado !== 'Cancelada');
            setObras(ativas.length ? ativas : lista);
        } catch (e) {
            console.error(e);
            setTrab(null);
            setMsg({ tipo: 'erro', texto: 'Erro ao carregar os registos.' });
        }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const editar = (r) => {
        setForm({
            data: r.data,
            horas: String(num(r.horas)),
            horas_extra: num(r.horas_extra) > 0 ? String(num(r.horas_extra)) : '',
            obra_id: r.obra_id || '',
            observacoes: r.observacoes || '',
        });
        setMsg(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const guardar = async (e) => {
        e.preventDefault();
        setSaving(true); setMsg(null);
        try {
            await api.post('/horas/me', {
                data: form.data,
                horas: form.horas,
                horas_extra: form.horas_extra === '' ? 0 : form.horas_extra,
                obra_id: form.obra_id || null,
                observacoes: form.observacoes || null,
            });
            const { dia } = fmtDia(form.data);
            setMsg({ tipo: 'ok', texto: `Guardado o dia ${dia}.` });
            setForm({ ...FORM_VAZIO, obra_id: form.obra_id });
            await carregar();
        } catch (err) {
            setMsg({ tipo: 'erro', texto: err.response?.data?.error || 'Erro ao guardar.' });
        } finally {
            setSaving(false);
        }
    };

    const apagar = async (r) => {
        const { dia } = fmtDia(r.data);
        if (!window.confirm(`Apagar o registo de ${dia}?`)) return;
        try {
            await api.delete(`/horas/me/${r.data}`);
            await carregar();
        } catch (err) {
            setMsg({ tipo: 'erro', texto: err.response?.data?.error || 'Erro ao apagar.' });
        }
    };

    const totalNormais = registos.reduce((a, r) => a + num(r.horas), 0);
    const totalExtra = registos.reduce((a, r) => a + num(r.horas_extra), 0);

    return (
        <div className="rh-mobile">
            <div className="rh-mobile-top">
                <button className="rh-mobile-back" onClick={() => navigate('/mobile-dashboard')}>← Voltar</button>
                <h1>⏱️ Registo de Horas</h1>
            </div>
            <p className="rh-mobile-sub">
                {trab === undefined ? 'A carregar…' : trab ? trab.nome : 'Conta sem trabalhador associado'}
            </p>

            {msg && <div className={`rh-msg ${msg.tipo}`}>{msg.texto}</div>}

            {trab === null ? (
                <div className="rh-aviso">
                    A sua conta ainda não está ligada a nenhum trabalhador, por isso não é possível registar horas.
                    Peça ao escritório para associar a conta à sua ficha de trabalhador.
                </div>
            ) : (
                <>
                    <form className="rh-card" onSubmit={guardar}>
                        <h2>Registar dia</h2>
                        <div className="rh-field">
                            <label>Data</label>
                            <input type="date" value={form.data} max={hojeISO()} onChange={set('data')} required />
                        </div>
                        <div className="rh-row">
                            <div className="rh-field">
                                <label>Horas</label>
                                <input type="number" inputMode="decimal" step="0.5" min="0" max="24" value={form.horas} onChange={set('horas')} required />
                            </div>
                            <div className="rh-field">
                                <label>Horas extra</label>
                                <input type="number" inputMode="decimal" step="0.5" min="0" max="24" value={form.horas_extra} onChange={set('horas_extra')} placeholder="0" />
                            </div>
                        </div>
                        <div className="rh-field">
                            <label>Obra</label>
                            <select value={form.obra_id} onChange={set('obra_id')}>
                                <option value="">— Sem obra / escritório —</option>
                                {obras.map(o => <option key={o.id} value={o.id}>{obraLabel(o)}</option>)}
                            </select>
                        </div>
                        <div className="rh-field">
                            <label>Observações</label>
                            <textarea value={form.observacoes} onChange={set('observacoes')} placeholder="Opcional" maxLength={500} />
                        </div>
                        <button className="rh-btn" type="submit" disabled={saving || trab === undefined}>
                            {saving ? 'A guardar…' : 'Guardar'}
                        </button>
                    </form>

                    <div className="rh-card">
                        <div className="rh-total">
                            <span>Últimos 30 dias</span>
                            <span><strong>{totalNormais}h</strong>{totalExtra > 0 && <> + <strong>{totalExtra}h</strong> extra</>}</span>
                        </div>
                        {registos.length === 0 ? (
                            <div className="rh-vazio">Ainda não há registos.</div>
                        ) : (
                            <ul className="rh-lista">
                                {registos.map(r => {
                                    const { dia, semana } = fmtDia(r.data);
                                    return (
                                        <li key={r.id} className="rh-item">
                                            <div className="rh-item-data"><b>{dia}</b><span>{semana}</span></div>
                                            <div className="rh-item-info">
                                                <b>{num(r.horas)}h</b>{num(r.horas_extra) > 0 && <> + {num(r.horas_extra)}h extra</>}
                                                <small>{r.obra_posicao ? `Obra ${r.obra_posicao}` : 'Sem obra'}{r.observacoes ? ` · ${r.observacoes}` : ''}</small>
                                            </div>
                                            <button type="button" onClick={() => editar(r)}>Editar</button>
                                            <button type="button" className="apagar" onClick={() => apagar(r)} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default RegistoHoras;
